import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import { Collapse, Typography } from "@mui/material";
import { History } from "lucide-react";

import BaseModal from "@reusables/Modals/BaseModal";
import BaseInput from "@reusables/BaseInput";
import { Product } from "@/types/general";
import { toastError } from "@helpers/utils";
import {
    useCreateProductReservationMutation,
    useDeleteProductReservationMutation,
    useUpdateProductReservationMutation
} from "@redux/features/products/productsApi";
import { isErrorWithMessage } from "@redux/api/query";
import UIButton from "@reusables/UIButton";
import BaseBadge from "@reusables/BaseBadge";

interface ReservationModalProps {
    isOpen: boolean;
    onClose: () => void;
    productId: number;
    productName: string;
    orderId?: number;
    orderCode?: string;
    reservation?: Product.Augmentations.Reservation.Root;
}


export default function ReservationModal({
                                             isOpen,
                                             onClose,
                                             productId,
                                             productName,
                                             orderId,
                                             orderCode,
                                             reservation
                                         }: ReservationModalProps) {
    const { t } = useTranslation("", { keyPrefix: "products.viewEditProduct.tabs.saleOrder.modals.reservation" });

    const [createReservation, { isLoading: isCreating }] = useCreateProductReservationMutation();
    const [updateReservation, { isLoading: isUpdating }] = useUpdateProductReservationMutation();
    const [deleteReservation, { isLoading: isDeleting }] = useDeleteProductReservationMutation();

    // TODO: this [0] is a temporary fix for the API returning an array of reservations
    const existingReservation = reservation?.reservations?.[0];
    const isEditing = !!existingReservation;
    const isLoading = isCreating || isUpdating || isDeleting;
    const maxQuantity = useMemo(() => existingReservation?.limit ?? 0, [existingReservation]);

    const schema = useMemo(() => z.object({
        quantity: z.coerce
            .number()
            .min(0)
    }).superRefine((data, ctx) => {
        // Only apply max constraint when editing (maxQuantity > 0)
        if (data.quantity > maxQuantity && isEditing) {
            ctx.addIssue({
                code: z.ZodIssueCode.too_big,
                maximum: maxQuantity,
                type: "number",
                inclusive: true,
                path: ["quantity"]
            });
        }
    }), [maxQuantity]);

    type FormData = z.infer<typeof schema>;

    const { control, handleSubmit, reset } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            quantity: existingReservation?.quantity ?? 0
        }
    });

    // Track current quantity to compare with original
    const currentQuantity = useWatch({ control, name: "quantity" });
    const originalQuantity = existingReservation?.quantity;
    const hasChanged = isEditing &&
        originalQuantity !== undefined &&
        Number(currentQuantity) !== originalQuantity;

    // Reset form when modal opens/closes or reservation changes
    useEffect(() => {
        if (isOpen) {
            reset({
                quantity: existingReservation?.quantity ?? 0
            });
        }
    }, [isOpen, existingReservation, reset]);

    const onSubmit = handleSubmit(async (data) => {
        if (!orderId) return;

        try {
            // If quantity is 0 and we're editing, delete the reservation
            if (isEditing && data.quantity === 0) {
                await deleteReservation({
                    product_id: productId,
                    reservation_id: existingReservation.id
                }).unwrap();

                toast.success(t("responses.deletion.success"));
            } else if (isEditing) {
                await updateReservation({
                    product_id: productId,
                    reservation_id: existingReservation.id,
                    quantity: data.quantity
                }).unwrap();

                toast.success(t("responses.editing.success"));
            } else {
                await createReservation({
                    product_id: productId,
                    sale_order_id: orderId,
                    quantity: data.quantity
                }).unwrap();

                toast.success(t("responses.creation.success"));
            }
            onClose();
        } catch (e) {
            if (isErrorWithMessage(e)) {
                toastError(e);
            } else {
                console.error("An error occurred while reserving product:", e);
            }
        }
    });

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            width={700}
            padding="56px"
            isLoading={isLoading}
            useCloseIcon
        >
            <form className="space-y-[32px]" onSubmit={onSubmit}>
                <div className="text-xl font-semibold text-center text-accent">
                    {isEditing ? t("heading.editing") : t("heading.creation")}
                </div>

                <div className="space-y-2">
                    {/* SO code */}
                    <div className="text-accent">
                        {t("subheading.order")}: <BaseBadge>{orderCode}</BaseBadge>
                    </div>

                    {/* Product name */}
                    <div className="text-accent">
                        {t("subheading.product")}: <span className="font-semibold">{productName}</span>
                    </div>
                </div>

                <div>
                    <Controller
                        name="quantity"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                label={t("fields.quantity.label")}
                                type="number"
                                value={field.value}
                                onChange={(val) => field.onChange(val)}
                                error={fieldState.error}
                                hideArrows
                                icon={maxQuantity > 0 ? {
                                    right: {
                                        el: (
                                            <span className="text-sm text-gray-400">
                                                / {maxQuantity}
                                            </span>
                                        ),
                                        offset: "14px"
                                    }
                                } : undefined}
                            />
                        )}
                    />

                    {/* Visual indicator of the previous value */}
                    <Collapse in={hasChanged}>
                        <div className="mt-2">
                            <Typography
                                variant="caption"
                                display="block"
                                sx={{ opacity: 0.5 }}
                                className="flex items-center space-x-2"
                            >
                                <History size={12} />
                                <span>{t("previousValue", { value: originalQuantity })}</span>
                            </Typography>
                        </div>
                    </Collapse>
                </div>

                <div className="grid grid-cols-2 gap-[16px]">
                    <UIButton size={"lg"} variant={"outline"} onClick={onClose}>
                        {t("buttons.cancel")}
                    </UIButton>
                    <UIButton size={"lg"} type="submit" loading={isLoading}>
                        {t("buttons.reserve")}
                    </UIButton>
                </div>
            </form>
        </BaseModal>
    );
}
